import client from './client';

export type ProductCategory =
  | 'eau_de_parfum'
  | 'eau_de_toilette'
  | 'car_perfume'
  | 'reed_diffuser'
  | 'candle'
  | 'accessory';

export interface FormulaItem {
  name: string;
  ml: number;
}

export interface Product {
  id: string;
  name: string;
  description: string | null;
  category: ProductCategory;
  mood: string | null;
  price: number;
  original_price: number | null;
  currency: string;
  size_ml: number | null;
  stock_qty: number;
  is_active: boolean;
  /** Primary shot — kept in step with images[0] by the service. */
  image_url: string | null;
  /** Full gallery, in display order. Resolved server-side, so legacy rows that
   *  only ever had image_url still arrive here as a one-item list. */
  images: string[];
  /** Opts the product into the landing page's Collection band. */
  show_in_landing: boolean;
  notes_top: string[];
  notes_middle: string[];
  notes_base: string[];
  tags: string[];
  /** Free-text feeling words ("romantic", "focus") shown under the description
   *  on the storefront. Descriptive only — not filterable, unlike `tags`. */
  mood_tags: string[];
  /** Admin-written copy for the three "Fragrance Details" accordion rows on the
   *  storefront product page. Plain text. null = that row keeps the site's
   *  auto-generated content (scent journey / concentration blurb / bottle). */
  accordion_smell: string | null;
  accordion_wear: string | null;
  accordion_bottle: string | null;
  formula_data: FormulaItem[] | null;
  rating: number;
  review_count: number;
  created_at: string;
}

export interface ProductStats {
  total: number;
  active: number;
  inactive: number;
  out_of_stock: number;
  no_formula: number;
  /** Active products flagged for the landing page's Collection band. */
  in_landing: number;
}

export interface ProductsResponse {
  products: Product[];
  total: number;
  stats: ProductStats;
}

export interface FormulaCheck {
  oil_name: string;
  required_ml: number;
  available_ml: number;
  in_inventory: boolean;
  sufficient: boolean;
}

// The ecommerce service already parses JSON columns and returns is_active as a
// boolean; normalize defensively so page code can trust the shape regardless.
function fromApi(row: any): Product {
  const arr = (v: any): string[] => (Array.isArray(v) ? v : []);
  return {
    id: row.id,
    name: row.name,
    description: row.description ?? null,
    category: row.category,
    mood: row.mood ?? null,
    price: Number(row.price) || 0,
    original_price: row.original_price != null ? Number(row.original_price) : null,
    currency: row.currency ?? 'INR',
    size_ml: row.size_ml != null ? Number(row.size_ml) : null,
    stock_qty: Number(row.stock_qty) || 0,
    is_active: Boolean(row.is_active),
    image_url: row.image_url ?? null,
    // Defensive fallback for a service that predates the images column: split
    // the legacy comma-joined image_url so the gallery editor still opens full.
    images: Array.isArray(row.images)
      ? arr(row.images).map((u) => String(u).trim()).filter(Boolean)
      : String(row.image_url ?? '').split(',').map((u) => u.trim()).filter(Boolean),
    show_in_landing: Boolean(row.show_in_landing),
    notes_top: arr(row.notes_top),
    notes_middle: arr(row.notes_middle),
    notes_base: arr(row.notes_base),
    tags: arr(row.tags),
    mood_tags: arr(row.mood_tags),
    accordion_smell: row.accordion_smell ?? null,
    accordion_wear: row.accordion_wear ?? null,
    accordion_bottle: row.accordion_bottle ?? null,
    formula_data: Array.isArray(row.formula_data)
      ? row.formula_data.map((f: any) => ({ name: f.name, ml: Number(f.ml) }))
      : null,
    rating: Number(row.rating) || 0,
    review_count: Number(row.review_count) || 0,
    created_at: row.created_at,
  };
}

export interface GetProductsParams {
  search?: string;
  category?: string;
  is_active?: '1' | '0' | 'all';
  limit?: number;
  offset?: number;
}

export async function getProducts(params: GetProductsParams = {}): Promise<ProductsResponse> {
  const res = await client.get('/api/admin/products', { params });
  const raw = res.data;
  return {
    products: (Array.isArray(raw?.products) ? raw.products : []).map(fromApi),
    total: Number(raw?.total) || 0,
    stats: { total: 0, active: 0, inactive: 0, out_of_stock: 0, no_formula: 0, in_landing: 0, ...(raw?.stats ?? {}) },
  };
}

export async function getProduct(id: string): Promise<Product> {
  const res = await client.get(`/api/admin/products/${encodeURIComponent(id)}`);
  return fromApi(res.data?.product ?? res.data);
}

// Payload accepts the app-facing shape; arrays / formula_data pass straight
// through to the ecommerce service which stringifies them.
export type ProductInput = Partial<
  Pick<
    Product,
    | 'name' | 'description' | 'category' | 'mood' | 'price' | 'original_price'
    | 'size_ml' | 'stock_qty' | 'is_active' | 'image_url' | 'images' | 'show_in_landing'
    | 'notes_top' | 'notes_middle' | 'notes_base' | 'tags' | 'mood_tags'
    | 'accordion_smell' | 'accordion_wear' | 'accordion_bottle' | 'formula_data'
  >
>;

export async function createProduct(data: ProductInput): Promise<Product> {
  const res = await client.post('/api/admin/products', data);
  return fromApi(res.data?.product ?? res.data);
}

export async function updateProduct(id: string, data: ProductInput): Promise<Product> {
  const res = await client.put(`/api/admin/products/${encodeURIComponent(id)}`, data);
  return fromApi(res.data?.product ?? res.data);
}

export async function deleteProduct(id: string): Promise<void> {
  await client.delete(`/api/admin/products/${encodeURIComponent(id)}`);
}

export async function restoreProduct(id: string): Promise<void> {
  await client.post(`/api/admin/products/${encodeURIComponent(id)}/restore`);
}

export async function updateStock(id: string, stockQty: number): Promise<void> {
  await client.patch(`/api/admin/products/${encodeURIComponent(id)}/stock`, { stock_qty: stockQty });
}

export async function checkFormula(
  id: string
): Promise<{ checks: FormulaCheck[]; all_available: boolean }> {
  const res = await client.get(`/api/admin/products/${encodeURIComponent(id)}/formula-check`);
  return {
    checks: Array.isArray(res.data?.checks) ? res.data.checks : [],
    all_available: Boolean(res.data?.all_available),
  };
}

export const CATEGORY_LABELS: Record<ProductCategory, string> = {
  eau_de_parfum: 'Eau de Parfum',
  eau_de_toilette: 'Eau de Toilette',
  car_perfume: 'Car Perfume',
  reed_diffuser: 'Reed Diffuser',
  candle: 'Candle',
  accessory: 'Accessory',
};

// Categories that carry an oil formula — others show "N/A" in the Formula column.
export const FORMULA_CATEGORIES: ProductCategory[] = ['eau_de_parfum', 'eau_de_toilette'];
